import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route } from 'react-router-dom';

import useInitSettings from './hooks/useInitSettings';
import { hideWizardOnFirstTime, saveSettings, selectCategories } from './containers/settingsSlice';
import { CATEGORIES } from './common/categories';
import { FEATURE_FLAGS_NAMES, ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { httpPost, isUserPermitted } from '../utils';
import { PricingModules, usePricingModulesState } from '../../context/contexts/PricingModuleContext';

import Sidebar from './components/Sidebar/Sidebar';
import NavBar from './components/NavBar/NavBar';

import General from './containers/General/General';
import ProtectedDomains from './containers/ProtectedDomains/ProtectedDomains';
import SilverfortNodes from './containers/SilverfortNodes/SilverfortNodes';
import MFATokens from './containers/MFATokensNew/MFATokens';
import DesktopApplication from './containers/DesktopApplication/DesktopApplication';
import Integrations from './containers/Integrations/Integrations';
import SilverfortApi from './containers/SilverfortApi/SilverfortApi';
import CustomInsights from './containers/CustomInsights/CustomInsights';
import Advanced from './containers/Advanced/Advanced';
import AlertsManagement from './containers/AlertsManagement/AlertsManagement';
import Licensing from './containers/Licensing/Licensing';
import AvailabilityZones from './containers/AvailabilityZones/AvailabilityZones';

import styles from './SystemSettings.module.css';
import useFeature from './hooks/featureFlags/useFeature';
import Security from './containers/Security/Security';
import PASSettings from './containers/PASSettings/PASSettings';
import UserManagementSettings from './containers/UserManagement';
import { useGetVisibleCategoryIds } from './hooks/lockedFeatures/useCategoryDisplay';
import CloudIntegrations from './containers/CloudIntegrations/CloudIntegrations';
import NhiSettings from './containers/Nhi/NhiSettings';

const SystemSettings = () => {
    const isLoading = useInitSettings(null);

    const user = useSelector((state) => state.userData.user);
    const menu = useSelector(selectCategories);
    const visibleCategoryIds = useGetVisibleCategoryIds();
    const [duringAction, setDuringAction] = useState(false);
    const [isDuringSave, setIsDuringSave] = useState(false);

    const storeDispatch = useDispatch();
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);

    const { pricingModulesState } = usePricingModulesState();
    const isCloudIntegrationsEnabled = pricingModulesState?.[PricingModules.BASE_PLATFORM].cloudEnabled || false;

    const filterMenu = (menu) => {
        if (!isCloudIntegrationsEnabled) {
            menu = menu.filter((page) => page.id !== CATEGORIES.CLOUD_INTEGRATIONS.ID);
        }
        return menu.filter((category) => visibleCategoryIds.includes(category.id));
    };
    const saveFunc = useCallback(
        async (newSettings, syncActiveDirectory = false, includeFullError) => {
            let savedSettings;
            storeDispatch(hideWizardOnFirstTime());
            try {
                setIsDuringSave(true);
                const payload = { ...newSettings };
                if (syncActiveDirectory) {
                    payload.syncActiveDirectory = true;
                }
                const { data } = await httpPost('/Settings/UpdateSettings', payload);
                savedSettings = data.settings;
                const reducedSavedSettings = {};
                for (const key in newSettings) {
                    reducedSavedSettings[key] = savedSettings[key];
                }
                storeDispatch(saveSettings(reducedSavedSettings));
            } catch (err) {
                console.error(err);
                if (includeFullError) {
                    return Promise.reject(err);
                }

                if (err.response && err.response.data && err.response.data.error) {
                    return Promise.reject(err.response.data.error);
                } else {
                    return Promise.reject('Error occurred!');
                }
            } finally {
                setIsDuringSave(false);
            }
            return savedSettings;
        },
        [storeDispatch]
    );

    const isEditor = isUserPermitted(user, 'settings', ROLES.EDITOR.value);
    const disableSave = !isEditor || duringAction || isDuringSave;
    if (isLoading || duringAction) {
        return (
            <div id="new-settings">
                <div className="silverfort-loader">
                    <div className="logo"></div>
                </div>
            </div>
        );
    }

    return (
        <div id="new-settings">
            <div className={styles.wrapper + ' z-[800]'}>
                <NavBar setIsDuringSave={setIsDuringSave} setDuringAction={setDuringAction} isEditor={isEditor} />
                <div className={styles.settingsContentContainer}>
                    <Sidebar items={filterMenu(menu)} />
                    <div className={styles.content}>
                        <Route path={`/Settings/${CATEGORIES.GENERAL.NAME}`}>
                            <General saveFunc={saveFunc} disableSave={disableSave} systemMode={systemMode} />
                        </Route>
                        <Route path={`/Settings/${CATEGORIES.SECURITY.NAME}`}>
                            <Security systemMode={systemMode} saveFunc={saveFunc} disableSave={disableSave} />
                        </Route>
                        <Route path={`/Settings/${CATEGORIES.PROTECTED_DOMAINS.NAME}`}>
                            <ProtectedDomains saveFunc={saveFunc} disableSave={disableSave} systemMode={systemMode} />
                        </Route>
                        {visibleCategoryIds.includes(CATEGORIES.SILVERFORT_NODES.ID) && (
                            <Route path={`/Settings/${CATEGORIES.SILVERFORT_NODES.NAME}`}>
                                <SilverfortNodes saveFunc={saveFunc} disableSave={disableSave} />
                            </Route>
                        )}
                        {visibleCategoryIds.includes(CATEGORIES.AVAILABILITY_ZONES.ID) && (
                            <Route path={`/Settings/${CATEGORIES.AVAILABILITY_ZONES.NAME}`}>
                                <AvailabilityZones saveFunc={saveFunc} disableSave={disableSave} />
                            </Route>
                        )}
                        {visibleCategoryIds.includes(CATEGORIES.MFA_TOKENS.ID) && (
                            <Route path={`/Settings/${CATEGORIES.MFA_TOKENS.NAME}`}>
                                <MFATokens
                                    systemMode={systemMode}
                                    saveFunc={saveFunc}
                                    disableSave={disableSave}
                                    user={user}
                                />
                            </Route>
                        )}
                        <Route path={`/Settings/${CATEGORIES.USER_MANAGEMENT.NAME}`}>
                            <UserManagementSettings saveFunc={saveFunc} />
                        </Route>
                        <Route path={`/Settings/${CATEGORIES.DESKTOP_APPLICATION.NAME}`}>
                            <DesktopApplication systemMode={systemMode} saveFunc={saveFunc} disableSave={disableSave} />
                        </Route>
                        <Route path={`/Settings/${CATEGORIES.INTEGRATIONS.NAME}`}>
                            <Integrations saveFunc={saveFunc} disableSave={disableSave} />
                        </Route>
                        {visibleCategoryIds.includes(CATEGORIES.PRIVILEGED_ACCESS.ID) && (
                            <Route path={`/Settings/${CATEGORIES.PRIVILEGED_ACCESS.NAME}`}>
                                <PASSettings saveFunc={saveFunc} disableSave={disableSave} />
                            </Route>
                        )}
                        {visibleCategoryIds.includes(CATEGORIES.NON_HUMAN_IDENTITIES.ID) && (
                            <Route path={`/Settings/${CATEGORIES.NON_HUMAN_IDENTITIES.NAME}`}>
                                <NhiSettings saveFunc={saveFunc} disableSave={disableSave} />
                            </Route>
                        )}
                        <Route path={`/Settings/${CATEGORIES.SILVERFORT_API.NAME}`}>
                            <SilverfortApi saveFunc={saveFunc} systemMode={systemMode} />
                        </Route>
                        <Route path={`/Settings/${CATEGORIES.CUSTOM_INSIGHTS.NAME}`}>
                            <CustomInsights saveFunc={saveFunc} disableSave={disableSave} />
                        </Route>
                        {visibleCategoryIds.includes(CATEGORIES.ADVANCED.ID) && (
                            <Route path={`/Settings/${CATEGORIES.ADVANCED.NAME}`}>
                                <Advanced systemMode={systemMode} saveFunc={saveFunc} disableSave={disableSave} />
                            </Route>
                        )}
                        <Route path={`/Settings/${CATEGORIES.LICENSING.NAME}`}>
                            <Licensing systemMode={systemMode} saveFunc={saveFunc} />
                        </Route>
                        <Route path={`/Settings/${CATEGORIES.ALERTS_MANAGEMENT.NAME}`}>
                            <AlertsManagement systemMode={systemMode} saveFunc={saveFunc} disableSave={disableSave} />
                        </Route>
                        {isCloudIntegrationsEnabled && (
                            <Route path={`/Settings/${CATEGORIES.CLOUD_INTEGRATIONS.NAME}`}>
                                <CloudIntegrations />
                            </Route>
                        )}
                        <Route exact path="/Settings">
                            <Redirect exact from="/Settings" to={`/Settings/${CATEGORIES.GENERAL.NAME}`} />
                        </Route>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SystemSettings;
